/**
 * Register a new phone after signing in with a backup code.
 *
 * Reached only from the sign-in page, which hands over a short-lived ticket
 * in router state — never in the URL, never in storage. The ticket can do one
 * thing: replace the authenticator. It is not a session; when this page
 * finishes, the person signs in normally with the new phone.
 *
 * The old authenticator keeps working until the new one is confirmed, so
 * leaving part-way changes nothing except the one backup code that was used.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Loader2, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { apiRequest } from '../services/api';
import { TENANT } from '../config';
import BackupCodesPanel from '../components/Auth/BackupCodesPanel';

type Handoff = { ticket?: string; remaining?: number } | null;
type Step = 'scan' | 'done' | 'expired' | 'missing';

export default function ReplaceAuthenticator() {
  const location = useLocation();
  const handoff = (location.state as Handoff) || null;
  const ticket = handoff?.ticket || '';

  const [step, setStep] = useState<Step>(ticket ? 'scan' : 'missing');
  const [qr, setQr] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const started = useRef(false);

  const brand = TENANT.branding.name;
  const support = TENANT.branding.supportEmail;

  const gone = (r: { code?: string }) => r.code === 'ACTIVATION_TICKET_INVALID' || r.code === 'ACTIVATION_STEP_OUT_OF_ORDER';

  const begin = useCallback(async () => {
    setBusy(true); setError('');
    const r = await apiRequest<{ qr: string; secret: string }>('POST', '/activation/replace/begin', { ticket });
    setBusy(false);
    if (r.success && r.data) { setQr(r.data.qr); setSecret(r.data.secret); return; }
    if (gone(r)) { setStep('expired'); return; }
    setError(r.status === undefined ? 'Could not reach the server. Try again.' : (r.message || 'The new-phone step could not be started.'));
  }, [ticket]);

  useEffect(() => {
    // Staging a secret spends nothing, but once per visit is enough.
    if (ticket && !started.current) { started.current = true; begin(); }
  }, [ticket, begin]);

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError('');
    const r = await apiRequest<{ replaced: boolean; backupCodes: string[] }>(
      'POST', '/activation/replace/confirm', { ticket, code: code.trim() }
    );
    setBusy(false);
    if (r.success && r.data) { setCodes(r.data.backupCodes || []); setStep('done'); return; }
    if (gone(r)) { setStep('expired'); return; }
    setError(r.message || 'That six-digit code was not accepted. Try the next one your app shows.');
  };

  return (
    <div className="min-h-screen bg-[#f6faf9] px-4 py-10 text-[#10252d]">
      <div className="mx-auto w-full max-w-lg">
        <div className="mb-7 flex items-center gap-3">
          <img src={TENANT.branding.logoPrimaryPath} alt={brand} className="max-h-9 max-w-[150px] object-contain" />
          <span className="text-sm font-medium text-[#60747b]">Register a new phone</span>
        </div>

        <div className="rounded-lg border border-[#d7e5e1] bg-white p-6 shadow-sm">
          {error && (
            <div role="alert" className="mb-5 rounded-md border border-[#e8c9c9] bg-[#fbeeee] px-4 py-3 text-sm text-[#b94343]">
              {error}
            </div>
          )}

          {step === 'missing' && (
            <div className="flex flex-col gap-3">
              <h1 className="text-xl font-semibold">Start from the sign-in page</h1>
              <p className="text-sm leading-6 text-[#60747b]">
                This page opens after you sign in with your password and a backup
                code. Nothing has changed on your account.
              </p>
              <Link to="/login" className="inline-flex h-11 items-center justify-center rounded-md bg-[#10252d] px-5 text-sm font-semibold text-white">
                Go to sign in
              </Link>
            </div>
          )}

          {step === 'expired' && (
            <div className="flex flex-col gap-3" role="alert">
              <h1 className="text-xl font-semibold">This step has expired</h1>
              <p className="text-sm leading-6 text-[#60747b]">
                It lasts 30 minutes and must be finished in one sitting. Your old
                authenticator was not changed. The backup code you used is spent — sign
                in again with a different one.
              </p>
              <Link to="/login" className="inline-flex h-11 items-center justify-center rounded-md bg-[#10252d] px-5 text-sm font-semibold text-white">
                Sign in again
              </Link>
            </div>
          )}

          {step === 'scan' && (
            <form onSubmit={confirm} className="flex flex-col gap-4">
              <div>
                <h1 className="text-xl font-semibold">Scan with your new phone</h1>
                <p className="mt-2 text-sm leading-6 text-[#60747b]">
                  First delete the old &ldquo;{brand}&rdquo; entry from your authenticator
                  app if it is still there. Then scan this and enter the six digits it shows.
                </p>
                {typeof handoff?.remaining === 'number' && (
                  <p className="mt-2 text-xs text-[#60747b]">
                    Backup code accepted. When you finish, you get a new set and the old ones stop working.
                  </p>
                )}
              </div>
              {busy && !qr && <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#60747b]" aria-label="Loading" />}
              {!busy && !qr && (
                <button type="button" onClick={begin} className="self-start rounded border border-[#d7e5e1] bg-white px-3 py-1.5 text-xs font-semibold">
                  Try again
                </button>
              )}
              {qr && <img src={qr} alt="New authenticator setup code" className="mx-auto h-44 w-44 rounded-md border border-[#d7e5e1] bg-white p-2" />}
              {secret && (
                <p className="text-center text-xs text-[#60747b]">
                  Can&apos;t scan? Enter this key manually:<br />
                  <span className="font-mono text-[11px] tracking-wider text-[#10252d]">{secret}</span>
                </p>
              )}
              <label className="text-sm font-medium">
                Six-digit code
                <input
                  required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric" autoComplete="one-time-code" placeholder="000000"
                  className="mt-1 h-11 w-full rounded-md border border-[#d7e5e1] px-3 text-center font-mono text-lg tracking-[0.3em] outline-none focus:border-[#17685d]"
                />
              </label>
              <button
                type="submit" disabled={busy || code.length !== 6 || !qr}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#10252d] px-5 text-sm font-semibold text-white disabled:opacity-60"
              >
                {busy && qr ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                Use this phone
              </button>
            </form>
          )}

          {step === 'done' && (
            <div className="flex flex-col gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#e9f3f0]">
                <CheckCircle2 className="h-6 w-6 text-[#17685d]" />
              </div>
              <div>
                <h1 className="text-xl font-semibold">Your new phone is registered</h1>
                <p className="mt-2 text-sm leading-6 text-[#60747b]">
                  You were signed out everywhere. Sign in with your password and a code from the new phone.
                  If you did not do this, contact {support || 'your administrator'} now.
                </p>
              </div>
              {codes.length > 0 && (
                <BackupCodesPanel codes={codes} onSaved={() => setSaved(true)} note="Your earlier backup codes no longer work." />
              )}
              {codes.length > 0 && !saved ? (
                <button type="button" disabled className="inline-flex h-11 items-center justify-center rounded-md bg-[#10252d] px-5 text-sm font-semibold text-white opacity-60">
                  Store your backup codes to continue
                </button>
              ) : (
                <a href="/login" className="inline-flex h-11 items-center justify-center rounded-md bg-[#10252d] px-5 text-sm font-semibold text-white">
                  Sign in
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
